import type { ChangeEvent } from "react";
import { useEffect, useId, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Navigate, useNavigate } from "react-router-dom";
import { jt, t } from "ttag";

import { EXTERNAL_LINK } from "@/config/external-links";
import { EVENT_PREFIX } from "@/features/analytics/models";
import { useCustomerIoContext } from "@/features/analytics/providers";
import { trackAffiliateIdCaptured } from "@/features/analytics/trackers/affiliate-id-captured";
import { trackEmailPhonePageViewed } from "@/features/analytics/trackers/email-phone-page-viewed";
import { hasEverflowAttribution } from "@/features/analytics/triggers/customer-io";
import { useCreateUser } from "@/features/onboarding/hooks";
import { SIGNUP_FUNNEL } from "@/features/onboarding/models";
import { useOnboardingStore } from "@/features/onboarding/stores";
import { productsQueriesStore } from "@/features/products/api";
import { LogoLoader } from "@/shared/components";
import { CATEGORY_SLUG } from "@/shared/domains";
import { GENDER, phoneNumberSchema } from "@/shared/models";
import { errorToast } from "@/shared/ui";
import { formatDOB } from "@/shared/utils";
import { FunnelStep } from "../components";
import { FUNNEL_ROUTE } from "../router/constants";
import { useFunnelStore } from "../stores";

const inputClassName =
  "w-full rounded-xl border border-neutral-300 bg-neutral-50 px-4 py-3.5 text-base text-neutral-900 outline-none placeholder:text-neutral-400 hover:border-neutral-500 focus:border-neutral-500 focus:ring-2 focus:ring-secondary-200 focus:ring-offset-1";

const CheckboxSquare = ({ checked }: { checked: boolean }) => (
  <span
    className={`mt-0.5 flex size-5 shrink-0 items-center justify-center rounded border-2 transition ${
      checked
        ? "border-secondary-500 bg-secondary-500 text-white"
        : "border-neutral-400 bg-white"
    }`}
  >
    {checked && (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={3}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="size-3.5"
        aria-hidden="true"
      >
        <path d="M20 6 9 17l-5-5" />
      </svg>
    )}
  </span>
);

export const Contact = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { analytics } = useCustomerIoContext();
  const set = useFunnelStore((store) => store.set);
  const setContactDetails = useOnboardingStore(
    (store) => store.setContactDetails,
  );
  const priceVariant = useOnboardingStore(
    (store) => store.urlParams.price_variant,
  );
  const couponCode = useOnboardingStore((store) => store.couponCode);
  const createUser = useCreateUser();

  const {
    firstName,
    lastName,
    state,
    heightFeet,
    heightInches,
    initialWeight,
    sex,
    dateOfBirth,
  } = useFunnelStore.getState();

  const [email, setEmail] = useState(useFunnelStore.getState().email ?? "");
  const [phone, setPhone] = useState(useFunnelStore.getState().phone ?? "");
  const [isEmailTouched, setIsEmailTouched] = useState(false);
  const [isPhoneTouched, setIsPhoneTouched] = useState(false);
  const emailErrorId = useId();
  const phoneErrorId = useId();
  const [confirmAccurate, setConfirmAccurate] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [termsAndConditionsConsent, setTermsAndConditionsConsent] =
    useState(false);

  useEffect(() => {
    trackEmailPhonePageViewed({
      customerIoAnalytics: analytics,
      eventPrefix: EVENT_PREFIX.FUNNEL,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!firstName || !lastName) {
    return <Navigate to={FUNNEL_ROUTE.MEDICAL_REVIEW} replace />;
  }

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const phoneValid = phoneNumberSchema.safeParse(phone).success;
  const isComplete =
    emailValid && phoneValid && confirmAccurate && termsAndConditionsConsent;

  const emailError =
    isEmailTouched && !emailValid ? t`Enter a valid email` : undefined;
  const phoneError =
    isPhoneTouched && !phoneValid
      ? t`Enter a valid 10-digit U.S. or Canadian phone number`
      : undefined;

  const handlePhoneChange = (event: ChangeEvent<HTMLInputElement>) => {
    const digits = event.target.value.replace(/\D/g, "");
    const nationalDigits =
      digits.length > 10 && digits.startsWith("1") ? digits.slice(1) : digits;
    if (nationalDigits.length > 10 && phone.length === 10) {
      return;
    }
    setPhone(nationalDigits.slice(0, 10));
  };

  const handleSubmit = () => {
    if (!isComplete) {
      return;
    }
    set({ email, phone });

    // Prefetches pricing.tsx's products query in parallel with the user
    // creation request, so it's already cached by the time we navigate there
    // and the page never shows its own loading spinner. Awaited before
    // navigating (below) so a slow prefetch can't lose the race.
    const productsPrefetch = queryClient
      .prefetchQuery(
        productsQueriesStore.products.category._ctx.products({
          categorySlug: CATEGORY_SLUG.WEIGHT_LOSS,
          priceVariant,
          couponCode,
        }),
      )
      .catch(() => undefined);

    createUser.mutate(
      {
        firstName,
        lastName,
        email,
        phoneNumber: phone,
        heightFeet,
        heightInches,
        weightInPounds: initialWeight,
        gender: sex === GENDER.FEMALE ? GENDER.FEMALE : GENDER.MALE,
        dateOfBirth: dateOfBirth
          ? formatDOB({
              year: Number(dateOfBirth.year),
              month: Number(dateOfBirth.month),
              day: Number(dateOfBirth.day),
            })
          : undefined,
        smsMarketingConsent: marketingConsent,
        emailMarketingConsent: false,
        eligibilityAnswerIds: [],
        funnel: SIGNUP_FUNNEL.INTAKE_ONBOARDING,
        priceVariant,
      },
      {
        onSuccess: (data) => {
          setContactDetails({
            billing: {
              address: "",
              apartmentNumber: "",
              city: "",
              zipCode: "",
            },
            shipping: {
              address: "",
              apartmentNumber: "",
              city: "",
              zipCode: "",
            },
            state: state ?? "",
            email: data.email,
            code: data.code,
            ehrId: data.ehrId,
            phoneNumber: phone,
            smsMarketingConsent: marketingConsent,
            emailMarketingConsent: false,
          });

          const everflowAttribution = {
            efTransactionId: data.efTransactionId,
            sourceId: data.sourceId,
            uid: data.uid,
            sub1: data.sub1,
            sub2: data.sub2,
            sub3: data.sub3,
            sub4: data.sub4,
            sub5: data.sub5,
          };

          if (data.affiliateId || hasEverflowAttribution(everflowAttribution)) {
            trackAffiliateIdCaptured({
              customerIoAnalytics: analytics,
              email: data.email,
              affiliateId: data.affiliateId,
              everflowAttribution,
            });
          }

          void productsPrefetch.then(() => navigate(FUNNEL_ROUTE.PRICING));
        },
        onError: (error) =>
          errorToast(
            error instanceof Error
              ? error.message
              : t`Something went wrong. Please try again.`,
          ),
      },
    );
  };

  const termsLink = (
    <a
      key="terms"
      href={EXTERNAL_LINK.TERMS_AND_CONDITIONS}
      target="_blank"
      rel="noreferrer"
      onClick={(event) => event.stopPropagation()}
      className="text-primary-950 underline"
    >
      {t`Terms of Service`}
    </a>
  );
  const privacyLink = (
    <a
      key="privacy"
      href={EXTERNAL_LINK.PRIVACY_AND_POLICY}
      target="_blank"
      rel="noreferrer"
      onClick={(event) => event.stopPropagation()}
      className="text-primary-950 underline"
    >
      {t`Privacy Policy`}
    </a>
  );
  const termsAndConditionsLink = (
    <a
      key="terms-and-conditions"
      href={EXTERNAL_LINK.TERMS_AND_CONDITIONS}
      target="_blank"
      rel="noreferrer"
      onClick={(event) => event.stopPropagation()}
      className="text-primary-950 underline"
    >
      {t`Terms & Conditions`}
    </a>
  );
  const telehealthConsentLink = (
    <a
      key="telehealth-consent"
      href={EXTERNAL_LINK.OPENLOOP_TELEHEALTH_CONSENT}
      target="_blank"
      rel="noreferrer"
      onClick={(event) => event.stopPropagation()}
      className="text-primary-950 underline"
    >
      {t`Telehealth Consent`}
    </a>
  );

  return (
    <FunnelStep
      title={t`${firstName}, how should we reach you?`}
      description={t`Your care team uses email and text for appointment updates and prescription status.`}
      nextLabel={t`Finish`}
      nextDisabled={!isComplete}
      isSubmitting={createUser.isPending}
      onSubmit={handleSubmit}
    >
      <LogoLoader show={createUser.isPending} fullscreen />

      <label className="block">
        <span className="mb-2 block text-sm font-medium text-primary-950">
          {t`Email Address`}
        </span>
        <input
          type="email"
          className={`${inputClassName} mf-masked mf-listen`}
          placeholder="you@example.com"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          onBlur={() => setIsEmailTouched(true)}
          aria-invalid={Boolean(emailError)}
          aria-describedby={emailError ? emailErrorId : undefined}
        />
      </label>
      {emailError && (
        <p
          id={emailErrorId}
          role="alert"
          className="mt-2 text-sm font-medium text-red-700"
        >
          {emailError}
        </p>
      )}

      <label className="mt-6 block">
        <span className="mb-2 block text-sm font-medium text-primary-950">
          {t`Phone Number`}
        </span>
        {/* Always show the +1 prefix, but only the 10 digits are stored/sent —
            the backend never receives the +1, matching the other onboardings. */}
        <div className="flex items-center rounded-xl border border-neutral-300 bg-neutral-50 px-4 focus-within:border-neutral-500 focus-within:ring-2 focus-within:ring-secondary-200 focus-within:ring-offset-1 hover:border-neutral-500">
          <span className="mr-2 text-base text-neutral-500">+1</span>
          <input
            type="tel"
            inputMode="numeric"
            className="mf-masked mf-listen w-full bg-transparent py-3.5 text-base text-neutral-900 outline-none placeholder:text-neutral-400"
            placeholder="(201) 555-0123"
            value={phone}
            onChange={handlePhoneChange}
            onBlur={() => setIsPhoneTouched(true)}
            aria-invalid={Boolean(phoneError)}
            aria-describedby={phoneError ? phoneErrorId : undefined}
          />
        </div>
      </label>
      {phoneError && (
        <p
          id={phoneErrorId}
          role="alert"
          className="mt-2 text-sm font-medium text-red-700"
        >
          {phoneError}
        </p>
      )}

      <button
        type="button"
        onClick={() => setConfirmAccurate((prev) => !prev)}
        className="mt-6 flex w-full items-start gap-3 text-left"
      >
        <CheckboxSquare checked={confirmAccurate} />
        <span className="text-sm text-primary-950">
          {jt`I confirm that I am the patient completing this intake form and that my answers are accurate and complete to the best of my knowledge. I understand the importance of providing accurate health information for my care. I agree to the ${termsLink} and ${privacyLink}.`}
        </span>
      </button>

      <button
        type="button"
        onClick={() => setTermsAndConditionsConsent((prev) => !prev)}
        className="mt-4 flex w-full items-start gap-3 text-left"
      >
        <CheckboxSquare checked={termsAndConditionsConsent} />
        <span className="text-sm text-primary-950">
          {jt`I understand and agree to LevelsRx's ${termsAndConditionsLink} and ${telehealthConsentLink}.`}
        </span>
      </button>

      <button
        type="button"
        onClick={() => setMarketingConsent((prev) => !prev)}
        className="mt-4 flex w-full items-start gap-3 text-left"
      >
        <CheckboxSquare checked={marketingConsent} />
        <span className="text-sm text-primary-950">
          {t`I consent to receive marketing text messages from LevelsRx at the phone number provided, including promotions, discounts, and product/service updates. Msg frequency varies. Msg & data rates may apply. Reply HELP for help, STOP to opt out. Consent is not a condition of purchase.`}
        </span>
      </button>
    </FunnelStep>
  );
};
